import { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';

const RATING_SCALE_NAMESPACE = 'performance-rating-scale';

const getDefaultRatingScale = () => ({
  '5': {
    label: 'Outstanding',
    description: 'Consistently exceeds expectations and demonstrates exceptional performance. Sets new standards and serves as a role model.',
    shortDesc: 'Exceptional performance',
    color: '#28a745'
  },
  '4': {
    label: 'Exceeds Expectations',
    description: 'Often exceeds targets and requirements. Shows initiative and consistently delivers high-quality results.',
    shortDesc: 'Above standard',
    color: '#24f05a'
  },
  '3': {
    label: 'Meets Expectations',
    description: 'Consistently meets all job requirements and objectives. Demonstrates solid, reliable performance.',
    shortDesc: 'Meets standard',
    color: '#ffc107'
  },
  '2': {
    label: 'Needs Improvement',
    description: 'Sometimes falls short of requirements. Requires guidance and development in certain areas.',
    shortDesc: 'Below standard',
    color: '#fd7e14'
  },
  '1': {
    label: 'Unsatisfactory',
    description: 'Does not meet minimum requirements. Significant improvement needed or performance improvement plan required.',
    shortDesc: 'Fails to meet standard',
    color: '#dc3545'
  },
  '0': {
    label: 'Not Rated',
    description: 'No rating provided yet.',
    shortDesc: 'Not evaluated',
    color: '#6c757d'
  }
});

const getParentOrgUnitId = async (engine, orgUnitId) => {
  if (!orgUnitId) return null;
  try {
    const response = await engine.query({
      orgUnit: {
        resource: `organisationUnits/${orgUnitId}`,
        params: { fields: 'path,parent[id]' }
      }
    });
    const path = response.orgUnit?.path;
    if (path) {
      const pathParts = path.split('/').filter(Boolean);
      if (pathParts.length >= 5) return pathParts[4];
      return response.orgUnit?.parent?.id || null;
    }
    return null;
  } catch (error) {
    console.error('Failed to get parent org unit:', error);
    return null;
  }
};

const getDataStoreKey = (orgId) => orgId ? `scale_${orgId}` : 'scale_global';

export const useRatingScale = (orgUnitId) => {
  const engine = useDataEngine();
  const [ratingScale, setRatingScale] = useState(getDefaultRatingScale());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadRatingScale = async () => {
      if (!orgUnitId) {
        setLoading(false);
        return;
      }

      try {
        // Get parent org unit for inheritance
        const parentOrgId = await getParentOrgUnitId(engine, orgUnitId);
        const orgsToCheck = [orgUnitId];
        if (parentOrgId && parentOrgId !== orgUnitId) orgsToCheck.push(parentOrgId);

        let loadedScale = null;
        
        // Check org-specific configurations
        for (const orgId of orgsToCheck) {
          const key = getDataStoreKey(orgId);
          try {
            const response = await engine.query({
              ratingScale: {
                resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
              }
            });
            const data = response.ratingScale;
            if (data && typeof data === 'object' && Object.keys(data).length > 0) {
              loadedScale = data;
              console.log(`Loaded rating scale from ${orgId}:`, loadedScale);
              break;
            }
          } catch (err) {
            // Continue to next org
          }
        }

        // If no org-specific config, try global
        if (!loadedScale) {
          try {
            const globalResponse = await engine.query({
              ratingScale: {
                resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`,
              }
            });
            loadedScale = globalResponse.ratingScale;
          } catch (err) {
            // Use defaults
          }
        }

        if (loadedScale && typeof loadedScale === 'object') {
          setRatingScale(loadedScale);
        } else {
          setRatingScale(getDefaultRatingScale());
        }
      } catch (err) {
        console.error('Failed to load rating scale:', err);
        setError(err);
        setRatingScale(getDefaultRatingScale());
      } finally {
        setLoading(false);
      }
    };

    loadRatingScale();
  }, [engine, orgUnitId]);

  return { ratingScale, loading, error };
};